import { type AbundanceElement, periodicTableMass, relativeAtomicMass } from './atomicModels';
import { type ExploreChallenge, type ModelField, type WorkedStep, firstModelError, formatModelNumber } from './modelInputContract';

/**
 * Learner-edited isotopic abundances → relative atomic mass (CT-INTERACT, chemistry/CLAUDE.md
 * "Interactive calculation standard"). Isotopic masses stay the reviewed AME2020 values in
 * atomicModels.ts; only the abundances are the learner's. Ar = Σ(isotopic mass × fraction).
 */

/** Typed percentages must total 100 % within this rounding allowance. */
export const ABUNDANCE_TOTAL_TOLERANCE_PERCENT = 0.05;
const PERCENT = 100;

export type AbundancePercents = Record<string, number>;

const isotopeKey = (massNumber: number) => `m${massNumber}`;

export function abundanceFields(element: AbundanceElement): ModelField[] {
  return element.isotopes.map((isotope) => ({
    key: isotopeKey(isotope.massNumber),
    label: `${element.symbol}-${isotope.massNumber} abundance`,
    unit: '%',
    min: 0,
    max: 100,
    step: 0.01,
  }));
}

/** The reviewed abundances as percentages, rounded to the 0.01 % input step. */
export function reviewedPercents(element: AbundanceElement): AbundancePercents {
  return Object.fromEntries(
    element.isotopes.map((isotope) => [isotopeKey(isotope.massNumber), Math.round(isotope.abundance * PERCENT * 1000) / 1000]),
  );
}

export interface AbundanceTerm {
  massNumber: number;
  isotopicMass: number;
  fraction: number;
  product: number;
}

export interface AbundanceExploreResult {
  ar: number;
  terms: AbundanceTerm[];
  /** The element with the learner's abundances, for the linked bar chart. */
  explored: AbundanceElement;
  tableValue: number | null;
  steps: WorkedStep[];
  summary: string;
}

const mass = (value: number) => value.toFixed(4);

/**
 * Relative atomic mass from the learner's abundances. Throws a specific message on a bad field or
 * when the abundances do not total 100 % (fail closed); never rescales the learner's numbers.
 */
export function calculateAbundanceExplore(element: AbundanceElement, percents: AbundancePercents): AbundanceExploreResult {
  const fieldError = firstModelError(abundanceFields(element), percents);
  if (fieldError) throw new Error(fieldError);
  const values = element.isotopes.map((isotope) => percents[isotopeKey(isotope.massNumber)]);
  const total = values.reduce((sum, value) => sum + value, 0);
  if (Math.abs(total - PERCENT) > ABUNDANCE_TOTAL_TOLERANCE_PERCENT) {
    throw new Error(`Abundances must total 100 %. They total ${formatModelNumber(total, 2)} % now.`);
  }
  const explored: AbundanceElement = {
    ...element,
    isotopes: element.isotopes.map((isotope, index) => ({ ...isotope, abundance: values[index] / PERCENT })),
  };
  const terms = explored.isotopes.map((isotope) => ({
    massNumber: isotope.massNumber,
    isotopicMass: isotope.isotopicMass,
    fraction: isotope.abundance,
    product: isotope.isotopicMass * isotope.abundance,
  }));
  // Same check and sum as the reviewed model; 0.05 % equals its 0.0005 fraction tolerance.
  const ar = relativeAtomicMass(explored);
  const tableValue = periodicTableMass(element);
  const steps: WorkedStep[] = [
    {
      id: 'fractions',
      title: 'Change each percentage to a fraction',
      formula: 'fraction = % ÷ 100',
      working: terms.map((term, index) => `${formatModelNumber(values[index], 3)} % → ${formatModelNumber(term.fraction, 5)}`).join('; '),
      status: 'done',
    },
    {
      id: 'total',
      title: 'Check the abundances total 100 %',
      formula: 'Σ % = 100 %',
      working: `${values.map((value) => formatModelNumber(value, 3)).join(' + ')} = ${formatModelNumber(total, 3)} %`,
      status: 'done',
    },
    ...terms.map((term) => ({
      id: `term-${term.massNumber}`,
      title: `${element.symbol}-${term.massNumber} contribution`,
      formula: 'isotopic mass × fraction',
      working: `${mass(term.isotopicMass)} u × ${formatModelNumber(term.fraction, 5)} = ${mass(term.product)} u`,
      status: 'done' as const,
    })),
    {
      id: 'sum',
      title: 'Add the contributions',
      formula: 'Ar = Σ(isotopic mass × fraction)',
      working: `Ar(${element.symbol}) = ${terms.map((term) => mass(term.product)).join(' + ')} = ${ar.toFixed(3)}`,
      status: 'done',
    },
  ];
  return {
    ar,
    terms,
    explored,
    tableValue,
    steps,
    summary: `Result: Ar(${element.symbol}) = ${ar.toFixed(3)}${tableValue === null ? '' : ` · periodic table ${tableValue}`}`,
  };
}

/** Sets the last isotope to the remainder so the abundances total 100 %, or null if impossible. */
export function fillRemainder(element: AbundanceElement, percents: AbundancePercents): AbundancePercents | null {
  const keys = element.isotopes.map((isotope) => isotopeKey(isotope.massNumber));
  const last = keys[keys.length - 1];
  const others = keys.slice(0, -1).reduce((sum, key) => sum + percents[key], 0);
  const remainder = Math.round((PERCENT - others) * 1000) / 1000;
  if (!Number.isFinite(remainder) || remainder < 0 || remainder > PERCENT) return null;
  return { ...percents, [last]: remainder };
}

// ---------------------------------------------------------------------------------------------
// Teaching goal (CT-INTERACT C7): one learning question and 3 tasks, for every element
// ---------------------------------------------------------------------------------------------

/** What the task checks read: the reviewed element and the result for the learner's abundances. */
export interface AbundanceChallengeInput {
  element: AbundanceElement;
  result: AbundanceExploreResult;
}

/** A fraction at or above this counts as a pure isotope (99.95 %). */
const PURE_FRACTION = 0.9995;
/** Abundances within 0.5 percentage points of each other count as an equal split. */
const EQUAL_SPLIT_TOLERANCE = 0.005;
const HALF = 0.5;

/** Index of the most abundant isotope; the first one wins a tie. */
function mostCommonIndex(fractions: number[]): number {
  return fractions.reduce((best, fraction, index) => (fraction > fractions[best] ? index : best), 0);
}

const fractionsOf = (element: AbundanceElement) => element.isotopes.map((isotope) => isotope.abundance);

export const ABUNDANCE_QUESTION = 'Why is the relative atomic mass of an element usually not a whole number?';

export const ABUNDANCE_CHALLENGES: ExploreChallenge<AbundanceChallengeInput>[] = [
  {
    id: 'pure-isotope',
    prompt: 'Make a sample of only one isotope (100 %). Is Ar now exactly its mass number?',
    hint: 'Type 100 for one isotope and 0 for the others.',
    isMet: ({ result }) => result.explored.isotopes.some((isotope) => isotope.abundance >= PURE_FRACTION),
    why: 'With only one isotope, Ar is that isotope’s measured mass, for example 34.969 for Cl-35. It is close to the mass number 35 but not equal: the mass number only counts protons and neutrons.',
  },
  {
    id: 'equal-split',
    prompt: 'Give every isotope the same abundance. Is Ar now the periodic-table value?',
    hint: 'Two isotopes: 50 % each. Three isotopes: 33.33 %, 33.33 % and 33.34 %.',
    isMet: ({ result }) => {
      const fractions = fractionsOf(result.explored);
      return Math.max(...fractions) - Math.min(...fractions) <= EQUAL_SPLIT_TOLERANCE;
    },
    why: 'Equal shares give the plain average of the isotopic masses, for chlorine (34.969 + 36.966) ÷ 2 = 35.967. The periodic table says 35.45, because real chlorine is about 3 parts Cl-35 to 1 part Cl-37.',
  },
  {
    id: 'swap-most-common',
    prompt: 'Make a different isotope the most common one (over 50 %, still a mixture). Which way does Ar move?',
    hint: 'Swap the two biggest abundances, then check the total is 100 %.',
    isMet: ({ element, result }) => {
      const fractions = fractionsOf(result.explored);
      const top = mostCommonIndex(fractions);
      return top !== mostCommonIndex(fractionsOf(element)) && fractions[top] > HALF && fractions[top] < PURE_FRACTION;
    },
    why: 'Ar is a weighted mean, so it always sits nearer the more common isotope. Swapping chlorine to 24.24 % Cl-35 and 75.76 % Cl-37 moves Ar from 35.45 up to 36.48.',
  },
];
